import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const ROICalculatorPanel = ({ onExport }) => {
  const [employees, setEmployees] = useState(100);
  const [currentHrCost, setCurrentHrCost] = useState(8000);
  const [manualProcessingHours, setManualProcessingHours] = useState(40);
  const [results, setResults] = useState(null);

  useEffect(() => {
    calculateROI();
  }, [employees, currentHrCost, manualProcessingHours]);

  const calculateROI = () => {
    // Determine billing tier based on employee count
    let monthlySubscriptionCost;
    let tierName;
    if (employees >= 1 && employees <= 10) {
      monthlySubscriptionCost = 500;
      tierName = 'Starter';
    } else if (employees >= 11 && employees <= 50) {
      monthlySubscriptionCost = 1500;
      tierName = 'Professional';
    } else if (employees >= 51 && employees <= 500) {
      monthlySubscriptionCost = 4500;
      tierName = 'Enterprise';
    } else {
      monthlySubscriptionCost = 13500;
      tierName = 'Enterprise Plus';
    }

    // Detailed savings calculations
    const automationSavings = employees * 2.5; // K2.5 per employee/month from automation
    const complianceSavings = employees * 1.8; // K1.8 per employee/month from compliance automation
    const timeSavings = currentHrCost * 0.35; // 35% time savings
    const penaltyAvoidance = employees * 0.8; // K0.8 per employee/month from penalty avoidance
    const errorReduction = employees * 1.2; // K1.2 per employee/month from error reduction
    
    const totalMonthlySavings = automationSavings + complianceSavings + timeSavings + penaltyAvoidance + errorReduction;
    const annualSavings = totalMonthlySavings * 12;
    const annualSubscriptionCost = monthlySubscriptionCost * 12;
    const netAnnualSavings = annualSavings - annualSubscriptionCost;
    const roi = ((netAnnualSavings / annualSubscriptionCost) * 100)?.toFixed(0);
    const paybackMonths = (annualSubscriptionCost / totalMonthlySavings)?.toFixed(1);
    const hoursSaved = manualProcessingHours * 0.75; // 75% time reduction

    setResults({
      tierName,
      monthlySavings: totalMonthlySavings?.toFixed(0),
      annualSavings: annualSavings?.toFixed(0),
      estimatedCost: monthlySubscriptionCost?.toFixed(0),
      annualCost: annualSubscriptionCost?.toFixed(0),
      netSavings: netAnnualSavings?.toFixed(0),
      roi: roi,
      paybackMonths: paybackMonths,
      hoursSaved: hoursSaved?.toFixed(1),
      breakdown: {
        automation: automationSavings?.toFixed(0),
        compliance: complianceSavings?.toFixed(0),
        time: timeSavings?.toFixed(0),
        penalties: penaltyAvoidance?.toFixed(0),
        errors: errorReduction?.toFixed(0)
      }
    });
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Icon name="Calculator" size={24} className="text-emerald-600 dark:text-emerald-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">ROI Calculator</h3>
        </div>
        <Button variant="outline" size="sm" iconName="Download" onClick={onExport}>
          Export
        </Button>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        Calculate your potential cost savings and return on investment with PNG HR Analytics platform.
      </p>
      {/* Input Fields */}
      <div className="space-y-4 mb-6">
        <Input
          type="number"
          label="Number of Employees"
          value={employees}
          onChange={(e) => setEmployees(Math.max(1, parseInt(e?.target?.value) || 1))}
          min="1"
          helperText="Total number of employees in your organization"
        />
        <Input
          type="number"
          label="Current Monthly HR Costs (PGK)"
          value={currentHrCost}
          onChange={(e) => setCurrentHrCost(Math.max(0, parseInt(e?.target?.value) || 0))}
          min="0"
          helperText="Total monthly HR operational costs"
        />
        <Input
          type="number"
          label="Manual Processing Hours per Week"
          value={manualProcessingHours}
          onChange={(e) => setManualProcessingHours(Math.max(0, parseInt(e?.target?.value) || 0))}
          min="0"
          helperText="Hours spent on manual HR tasks weekly"
        />
      </div>
      {/* Results */}
      {results && (
        <div className="space-y-4">
          {/* Subscription Tier */}
          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4 border border-emerald-200 dark:border-emerald-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Recommended Plan</span>
              <Icon name="Award" size={18} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{results?.tierName}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              K{parseInt(results?.estimatedCost)?.toLocaleString()}/month
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="TrendingUp" size={16} className="text-emerald-500" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Monthly Savings</span>
              </div>
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                K{parseInt(results?.monthlySavings)?.toLocaleString()}
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Calendar" size={16} className="text-emerald-500" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Annual Savings</span>
              </div>
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                K{parseInt(results?.annualSavings)?.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Savings Breakdown */}
          <div className="border border-gray-200 dark:border-slate-600 rounded-lg p-4">
            <div className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Savings Breakdown</div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Administrative Overhead</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  K{parseInt(results?.breakdown?.automation)?.toLocaleString()}/mo
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Compliance Automation</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  K{parseInt(results?.breakdown?.compliance)?.toLocaleString()}/mo
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Time Savings</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  K{parseInt(results?.breakdown?.time)?.toLocaleString()}/mo
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Penalty Avoidance</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  K{parseInt(results?.breakdown?.penalties)?.toLocaleString()}/mo
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Error Reduction</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  K{parseInt(results?.breakdown?.errors)?.toLocaleString()}/mo
                </span>
              </div>
            </div>
          </div>

          {/* ROI Metrics */}
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 rounded-lg p-4 border border-emerald-200 dark:border-emerald-700">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">ROI</div>
                <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{results?.roi}%</div>
              </div>
              <div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Payback Period</div>
                <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                  {results?.paybackMonths} mo
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Hours Saved</div>
                <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                  {results?.hoursSaved}/wk
                </div>
              </div>
            </div>
          </div>

          {/* Net Savings */}
          <div className="bg-emerald-600 dark:bg-emerald-700 rounded-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-90 mb-1">Net Annual Savings</div>
                <div className="text-3xl font-bold">K{parseInt(results?.netSavings)?.toLocaleString()}</div>
                <div className="text-xs opacity-75 mt-1">
                  After K{parseInt(results?.annualCost)?.toLocaleString()} annual subscription cost
                </div>
              </div>
              <Icon name="PiggyBank" size={40} className="opacity-80" />
            </div>
          </div>
        </div>
      )}
      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-slate-600">
        <p className="text-xs text-gray-500 dark:text-gray-400 italic">
          * Calculations based on PNG market conditions, typical compliance automation savings, and verified customer data. Actual results may vary.
        </p>
      </div>
    </div>
  );
};

export default ROICalculatorPanel;